(() => {
  if (customElements.get('rad-drop-alerts')) return;

  customElements.define('rad-drop-alerts', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.teaser = this.querySelector('[data-drop-teaser]');
      this.preview = this.dataset.preview === 'true';
      this.email = '';
      this.step = 'email';
      this.busy = false;
      this.previousFocus = null;
      const listen = (target, event, fn) => target.addEventListener(event, fn, { signal: this.abort.signal });
      this.querySelector('[data-preview-note]').hidden = !this.preview;
      this.querySelector('[data-demo-complete]').hidden = !this.preview;
      this.querySelector('[data-live-complete]').hidden = this.preview;
      this.querySelector('[data-steps]').hidden = this.dataset.sms !== 'true';
      this.querySelectorAll('[data-close]').forEach(button => listen(button, 'click', () => this.close()));
      listen(this.querySelector('[data-skip-sms]'), 'click', () => this.showStep('complete'));
      listen(this.querySelector('[data-email-form]'), 'submit', event => this.submit(event, 'email'));
      listen(this.querySelector('[data-sms-form]'), 'submit', event => this.submit(event, 'sms'));
      listen(this.dialog, 'cancel', () => { this.dismissed = true; });
      listen(this.dialog, 'close', () => this.onClose());
      listen(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      });
      listen(document, 'shopify:section:select', event => {
        if (this.preview && event.target.contains(this)) this.open();
      });
      listen(document, 'shopify:section:deselect', event => {
        if (this.preview && event.target.contains(this)) this.dialog.close();
      });
      listen(this.teaser.querySelector('button'), 'click', () => {
        clearTimeout(this.timer);
        if (this.dialog.open) this.focusHeading();
        else this.open();
        this.renderTeaser();
      });
      this.teaserObserver = new MutationObserver(() => this.scheduleTeaser());
      this.teaserObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'aria-hidden'] });
      listen(document, 'toggle', () => this.scheduleTeaser());
      listen(window, 'resize', () => this.scheduleTeaser());
      listen(document, 'rad:drop-alerts:ready', () => { this.renderTeaser(); this.schedule(); });
      this.renderTeaser();
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.requestAbort?.abort();
      cancelAnimationFrame(this.teaserFrame);
      this.teaserObserver.disconnect();
      this.teaser.remove();
      this.abort.abort();
      if (this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('rad-drop-alerts-open');
    }

    saved() {
      try { return JSON.parse(localStorage.getItem('rad-drop-alerts') || '{}'); }
      catch { return {}; }
    }

    scheduleTeaser() {
      if (this.teaserFrame) return;
      this.teaserFrame = requestAnimationFrame(() => { this.teaserFrame = 0; this.renderTeaser(); });
    }

    renderTeaser() {
      const available = this.preview || this.dataset.enabled === 'true' && this.hasProvider();
      if (this.teaser.hidden !== !available) this.teaser.hidden = !available;
      if (!available || !this.isConnected) return;
      const modal = this.dialog.open ? this.dialog : [...document.querySelectorAll('dialog:modal')].at(-1);
      const parent = modal || document.body;
      if (this.teaser.parentElement !== parent) {
        if (typeof this.teaser.hidePopover === 'function' && this.teaser.matches(':popover-open')) this.teaser.hidePopover();
        parent.append(this.teaser);
      }
      if (typeof this.teaser.showPopover === 'function') {
        if (!this.teaser.matches(':popover-open')) this.teaser.showPopover();
      } else {
        const rect = modal?.getBoundingClientRect();
        this.teaser.style.left = `${rect ? -rect.left : 0}px`;
        this.teaser.style.top = `${innerHeight * .47 - (rect?.top || 0)}px`;
      }
    }

    remember(key) {
      if (this.preview) return;
      try { localStorage.setItem('rad-drop-alerts', JSON.stringify({ ...this.saved(), [key]: Date.now() })); }
      catch {}
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.preview || this.dialog.open || this.dismissed || this.dataset.enabled !== 'true' || !this.hasProvider()) return;
      if (/\/(cart|checkout|account)(\/|$)/.test(location.pathname)) return;
      const saved = this.saved();
      const days = (Date.now() - (saved.dismissed || 0)) / 86400000;
      if (saved.submitted || days < Number(this.dataset.frequency || 7)) return;
      this.timer = setTimeout(() => this.tryOpen(), Number(this.dataset.delay || 8) * 1000);
    }

    tryOpen() {
      const active = document.activeElement;
      const typing = active?.matches('input, textarea, select, [contenteditable="true"]');
      const blocked = document.hidden || typing || document.querySelector('dialog[open], .Drawer[aria-hidden="false"], [aria-modal="true"]');
      if (blocked) { this.timer = setTimeout(() => this.tryOpen(), 2000); return; }
      this.open();
    }

    open() {
      if (!this.isConnected || this.dialog.open) return;
      this.previousFocus = document.activeElement;
      this.dialog.showModal();
      this.renderTeaser();
      document.documentElement.classList.add('rad-drop-alerts-open');
      this.focusHeading();
    }

    close() {
      this.dismissed = true;
      this.dialog.close();
    }

    onClose() {
      this.dismissed = true;
      this.requestAbort?.abort();
      clearTimeout(this.timer);
      document.documentElement.classList.remove('rad-drop-alerts-open');
      this.remember('dismissed');
      this.querySelectorAll('input').forEach(input => { if (input.type === 'checkbox') input.checked = false; else input.value = ''; });
      this.email = '';
      this.step = 'email';
      this.querySelectorAll('[data-step]').forEach(element => { element.hidden = element.dataset.step !== 'email'; });
      this.querySelectorAll('[data-marker]').forEach(element => element.classList.toggle('is-current', element.dataset.marker === 'email'));
      this.querySelector('[data-steps]').hidden = this.dataset.sms !== 'true';
      this.querySelectorAll('[data-error-message]').forEach(element => { element.hidden = true; element.textContent = ''; });
      this.renderTeaser();
      this.previousFocus?.focus({ preventScroll: true });
    }

    focusHeading() {
      const title = this.querySelector(`[data-step="${this.step}"] h2`);
      this.dialog.setAttribute('aria-labelledby', title.id);
      title.focus({ preventScroll: true });
      this.dialog.scrollTop = 0;
    }

    showStep(step) {
      this.step = step;
      this.querySelectorAll('[data-step]').forEach(element => { element.hidden = element.dataset.step !== step; });
      this.querySelectorAll('[data-marker]').forEach(element => element.classList.toggle('is-current', element.dataset.marker === step));
      this.querySelector('[data-steps]').hidden = step === 'complete' || this.dataset.sms !== 'true';
      this.focusHeading();
    }

    hasProvider() {
      return typeof window.RadDropAlerts?.subscribe === 'function' || /^[A-Za-z0-9]{6}$/.test(this.dataset.siteId || '') && /^[A-Za-z0-9]{6}$/.test(this.dataset.emailList || '');
    }

    async subscribe(data) {
      if (typeof window.RadDropAlerts?.subscribe === 'function') return window.RadDropAlerts.subscribe(data);
      const list = data.channel === 'sms' ? this.dataset.smsList : this.dataset.emailList;
      if (!this.hasProvider() || !/^[A-Za-z0-9]{6}$/.test(list || '')) throw new Error('Unavailable');
      if (data.channel === 'sms' && (!data.smsConsent || this.dataset.smsReady !== 'true')) throw new Error('SMS not configured');
      const profile = {
        email: data.email,
        subscriptions: { [data.channel]: { marketing: { consent: 'SUBSCRIBED' } } },
        properties: { rad_drop_alerts_consent_version: '2026-10-22' }
      };
      if (data.channel === 'sms') profile.phone_number = data.phone;
      const controller = new AbortController();
      const abort = () => controller.abort();
      data.signal.addEventListener('abort', abort, { once: true });
      if (data.signal.aborted) controller.abort();
      const timeout = setTimeout(abort, 15000);
      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.dataset.siteId)}`, {
          method: 'POST',
          headers: { accept: 'application/vnd.api+json', 'content-type': 'application/vnd.api+json', revision: '2026-10-15' },
          credentials: 'omit',
          signal: controller.signal,
          body: JSON.stringify({ data: { type: 'subscription', attributes: { custom_source: `${data.source} / ${data.channel}`, profile: { data: { type: 'profile', attributes: profile } } }, relationships: { list: { data: { type: 'list', id: list } } } } })
        });
        if (response.status !== 202) throw new Error('Not accepted');
        return { status: 'accepted' };
      } finally {
        clearTimeout(timeout);
        data.signal.removeEventListener('abort', abort);
      }
    }

    async submit(event, channel) {
      event.preventDefault();
      if (this.busy || !event.target.reportValidity()) return;
      const form = event.target;
      const error = form.querySelector('[data-error-message]');
      const button = form.querySelector('[type="submit"]');
      error.hidden = true;
      let phone = '';
      if (channel === 'sms') {
        phone = form.elements.phone.value.replace(/[\s().-]/g, '');
        if (!/^\+1[2-9]\d{2}[2-9]\d{6}$/.test(phone)) { error.textContent = this.dataset.phoneError; error.hidden = false; return; }
      }
      const email = channel === 'email' ? form.elements.email.value.trim() : this.email;
      const original = button.innerHTML;
      const generation = this.abort;
      this.requestAbort = new AbortController();
      this.busy = true;
      button.disabled = true;
      button.textContent = this.dataset.sending;
      form.setAttribute('aria-busy', 'true');
      try {
        if (!this.preview) {
          const result = await this.subscribe({ channel, email, ...(phone ? { phone, smsConsent: true } : {}), source: 'Radical Riot drop alerts', signal: this.requestAbort.signal });
          if (result?.status !== 'accepted') throw new Error('Not accepted');
        }
        if (generation.signal.aborted || !this.dialog.open) return;
        this.remember('submitted');
        if (channel === 'email') {
          this.email = email;
          const smsReady = this.preview || this.dataset.smsReady === 'true';
          this.showStep(this.dataset.sms === 'true' && smsReady ? 'sms' : 'complete');
        } else this.showStep('complete');
      } catch (failure) {
        if (generation.signal.aborted || !this.dialog.open) return;
        error.textContent = this.dataset.error;
        error.hidden = false;
      } finally {
        this.busy = false;
        button.disabled = false;
        button.innerHTML = original;
        form.removeAttribute('aria-busy');
      }
    }
  });
})();
